import { Button, Card, Checkbox, InputNumber, Modal, Table } from "antd";
import {
  getDocument,
  getPDFDocument,
  getPDF2Document,
  updatePrintDownloadStatus
} from "api/services/JobService";
import { TJob, TTextControlJobDetails } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import React, { useEffect, useRef, useState } from "react";
import config from "../../account-config.json";

interface DownloadModalProps {
  isVisible: boolean;
  handleClose: () => void;
  strSelectedJobs: string[];
  strJobDetails: TJob[];
  source: string;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  isVisible,
  handleClose,
  strSelectedJobs,
  strJobDetails,
  source
}) => {
  const [mergeDownloadChecked, setMergeDownloadChecked] = useState<boolean>(false);
  const [spaceBetweenDocuments, setSpaceBetweenDocuments] = useState<number | null>(0);
  const [fileFormatOptionsChecked, setFileFormatOptionsChecked] = useState<string[]>(["Editable Document"]);
  const [abstractFileFormatOptionsChecked, setAbstractFileFormatOptionsChecked] = useState<string[]>(["Abstract – Editable (doc/rtf)"]);
  const { accountId, webDUser } = useTDAuthentication();
  const userId = webDUser?.user.userId;
  const fileFormatOptions = ["Editable Document", "PDF"];
  const abstractFileFormatOptions = ["Abstract – Editable (doc/rtf)", "Abstract – Linked PDF"];

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [downloadStatus, setDownloadStatus] = useState<{
    [key: string]: { status: string; progress?: number };
  }>({});
  const [downloadDirectory, setDownloadDirectory] = useState<any | null>(null);
  const [downloadedCount, setDownloadedCount] = useState<number>(0);
  const [shouldStartDownload, setShouldStartDownload] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const showMergeOptions = strSelectedJobs.length > 1;
  const isPdfFormatDisabled =
    !!accountId && config["pdfDisabled"].includes(accountId);
  const isPdf2FormatEnabled =
    !!accountId && config["pdf2Enabled"].includes(accountId);
  const isDocFormatDisabled =
    !!accountId && config["docDisabled"].includes(accountId);
  const isDoc2FormatEnabled =
    !!accountId && config["doc2Enabled"].includes(accountId);

  useEffect(() => {
    if (isVisible) {
      const mergeCheckbox = localStorage.getItem("mergeDownloadCheckbox") === "true";
      const spaceValue = localStorage.getItem("spaceBetweenDocumentsForDownload");

      setMergeDownloadChecked(mergeCheckbox);
      setSpaceBetweenDocuments(spaceValue ? parseInt(spaceValue) : 0);
    } else {
      resetModal();
    }
  }, [isVisible]);

  const resetModal = () => {
    setMergeDownloadChecked(false);
    setSpaceBetweenDocuments(0);
    setCurrentPage(1);
    setDownloadedCount(0);
    setDownloadStatus({});
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const saveDownloadSettings = () => {
    localStorage.setItem("mergeDownloadCheckbox", JSON.stringify(mergeDownloadChecked));
    localStorage.setItem(
      "spaceBetweenDocumentsForDownload",
      spaceBetweenDocuments?.toString() || "0"
    );
  };


  const handleSelectDirectory = async () => {
    try {
      //@ts-ignore
      const directoryHandle = await window.showDirectoryPicker();
      setDownloadDirectory(directoryHandle);
      saveDownloadSettings();
      setCurrentPage(2);
      setShouldStartDownload(true);
    } catch (error) {
      console.error("Error selecting directory:", error);
    }
  };

  useEffect(() => {
    if (shouldStartDownload) {
      startDownloadProcess();
      setShouldStartDownload(false);
    }
  }, [shouldStartDownload]);

  const startDownloadProcess = async () => {
    const jobIdsArray = strSelectedJobs;
    let fileFormatOptions = isDoc2FormatEnabled && isPdf2FormatEnabled ? abstractFileFormatOptionsChecked : fileFormatOptionsChecked;

    for (const fileFormat of fileFormatOptions) {
      if (!mergeDownloadChecked) {
        for (const jobId of jobIdsArray) {
          const jobDetail = strJobDetails.find(job => job.jobId.toString() === jobId);
          if (!jobDetail) continue;

          try {
            abortControllerRef.current = new AbortController();

            const jobDetails: TTextControlJobDetails = {
              jobIds: [jobId],
              accountId: accountId ?? "",
              userId: userId ?? "",
              jobAction: "Download",
              connectionId: null,
              mergeDetails: {
                isMerge: mergeDownloadChecked,
                noOfLines: spaceBetweenDocuments ?? 0
              }
            };

            let functionToExecute;
            switch(fileFormat){
              case "PDF":
                functionToExecute = await getPDFDocument(jobDetails);
                break;
              case "Abstract – Linked PDF":
                functionToExecute = await getPDF2Document(jobDetails);
                break;
              case "Editable Document":
                functionToExecute = await getDocument(jobDetails)
                break;
              case "Abstract – Editable (doc/rtf)":
                functionToExecute = await getDocument(jobDetails)
                break;
              default:
                functionToExecute = await getDocument(jobDetails)
                break;
            }
            const { blob, filename, contentLength } = functionToExecute

            let fileHandle = await getUniqueFileHandle(filename);

            const writableStream = await fileHandle.createWritable();
            const reader = blob.stream().getReader();
            let downloadedBytes = 0;

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              downloadedBytes += value.length;
              await writableStream.write(value);

              let progressPercentage = Math.floor((downloadedBytes / contentLength) * 100);
              setDownloadStatus(prev => ({
                ...prev,
                [`${jobId}-${fileFormat}`]: { status: `Downloading...`, progress: progressPercentage }
              }));
            }

            await writableStream.close();
            setDownloadedCount(prev => prev + 1);
            setDownloadStatus(prev => ({
              ...prev,
              [`${jobId}-${fileFormat}`]: { status: `Download Complete`, progress: undefined }
            }));
            await updatePrintDownloadStatus([jobId], accountId || "");
          } catch (error) {
            console.log(error);
            setDownloadStatus(prev => ({
              ...prev,
              [`${jobId}-${fileFormat}`]: { status: `Download Failed`, progress: undefined }
            }));
          }
        }
      } else {
        try {
          abortControllerRef.current = new AbortController();

          const jobDetails: TTextControlJobDetails = {
            jobIds: jobIdsArray,
            accountId: accountId || "",
            userId: userId || "",
            jobAction: "Download",
            connectionId: null,
            mergeDetails: {
              isMerge: mergeDownloadChecked,
              noOfLines: spaceBetweenDocuments ?? 0
            }
          };

          let functionToExecute;
            switch(fileFormat){
              case "PDF":
                functionToExecute = await getPDFDocument(jobDetails);
                break;
              case "Abstract – Linked PDF":
                functionToExecute = await getPDF2Document(jobDetails);
                break;
              case "Editable Document":
                functionToExecute = await getDocument(jobDetails)
                break;
              case "Abstract – Editable (doc/rtf)":
                functionToExecute = await getDocument(jobDetails)
                break;
              default:
                functionToExecute = await getDocument(jobDetails)
                break;
            }
            const { blob, filename, contentLength } = functionToExecute

          let patientNames = strJobDetails
            .filter(job => jobIdsArray.includes(job.jobId.toString()))
            .map(job => job.patientName)
            .join(" ");

          let fileHandle = await getUniqueFileHandle(filename);
          const writableStream = await fileHandle.createWritable();
          const reader = blob.stream().getReader();
          let downloadedBytes = 0;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            downloadedBytes += value.length;
            await writableStream.write(value);

            let progressPercentage = Math.floor((downloadedBytes / contentLength) * 100);
            setDownloadStatus(prev => ({
              ...prev,
              [`${strSelectedJobs.join(",")}-${fileFormat}`]: {
                status: `Downloading...`,
                progress: progressPercentage
              }
            }));
          }

          await writableStream.close();

          setDownloadedCount(1);
          setDownloadStatus({
            [`${strSelectedJobs.join(",")}-${fileFormat}`]: {
              status: `Merged Download Complete for Patients: ${patientNames}`,
              progress: undefined
            }
          });
          await updatePrintDownloadStatus(jobIdsArray, accountId || "");
        } catch (error) {
          console.log(error);
          setDownloadStatus({
            [`${strSelectedJobs.join(",")}-${fileFormat}`]: { status: "Merged Download Failed", progress: undefined }
          });
        }
      }
    }
  };

  const getUniqueFileHandle = async (fileName: string) => {
    console.log("reached file save");
    let originalName = fileName.split(".")[0];
    let extension = fileName.split(".").pop();
    let counter = 0;
    const maxAttempts = 30;
    while (counter <= maxAttempts) {
      const currentFileName =
        counter === 0 ? fileName : `${originalName}(${counter}).${extension}`;
      console.log(`Attempting to get file handle for ${currentFileName}`);

      try {
        const fileHandle = await downloadDirectory?.getFileHandle(currentFileName);
        if (fileHandle) {
          console.log(`File ${currentFileName} already exists. Renaming...`);
          counter++;
          continue;
        }
        throw new Error("File handle not found");
      } catch (error) {
        console.log(
          `File ${currentFileName} does not exist or cannot be accessed. Creating...`
        );
        try {
          const fileHandle = await downloadDirectory?.getFileHandle(currentFileName, {
            create: true
          });
          console.log("file name is ", currentFileName);
          return fileHandle;
        } catch (createError) {
          console.error("Error creating file:", createError);
          counter++;
        }
      }
    }
    throw new Error(`Failed to create unique file after ${maxAttempts} attempts.`);
  };

  return (
    <Modal
      title="TransDyne Download Manager"
      visible={isVisible}
      onCancel={handleClose}
      footer={null}
      className="custom-modal"
      centered={true}>
      {currentPage === 1 && (
        <div >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems:"flex-end", marginBottom:".75rem"}}>
            
            {showMergeOptions && (
              <div style={{width:"50%"}}>
                <label htmlFor="no-of-lines" className="input-label">
                  Lines of space between documents
                </label>
                <InputNumber
                  id="no-of-lines"
                  disabled={!mergeDownloadChecked}
                  value={spaceBetweenDocuments}
                  onChange={value =>
                    setSpaceBetweenDocuments(value !== undefined ? value : null)
                  }
                  className="filter-input"
                  placeholder="Space Between Documents"
                />
              </div>
            )}
            <div className="file-format-selection" >
              <label htmlFor="report-type" className="input-label" style={{width:"15rem"}}>
                File Format
              </label>
              {!isDocFormatDisabled && !isPdfFormatDisabled && (
                <Checkbox.Group
                  options={fileFormatOptions}
                  defaultValue={["Editable Document"]}
                  onChange={(checkedValues) => setFileFormatOptionsChecked(checkedValues)}
                />
              )}
              {isDoc2FormatEnabled && isPdf2FormatEnabled && (
                <Checkbox.Group
                  options={abstractFileFormatOptions}
                  defaultValue={["Abstract – Editable (doc/rtf)"]}
                  onChange={(checkedValues) => setAbstractFileFormatOptionsChecked(checkedValues)}
                />
              )}
            </div>
          </div>
          {showMergeOptions && (
              <Checkbox
                checked={mergeDownloadChecked}
                onChange={e => setMergeDownloadChecked(e.target.checked)}
                className="merge-checkbox">
                Merge Files
              </Checkbox>
          )}

          <div className="action-buttons" style={{display:'flex', justifyContent:"flex-end"}}>
            <Button type="primary" onClick={handleSelectDirectory}>
              Download
            </Button>
          </div>
        </div>
      )}

      {currentPage === 2 && (
        <div className="download-status-table">
          <Card className="card-style" styles={{body:{padding:"0"}}}>
          <Table
          style={{border:"none"}}
          className="table-style"
          components={{
            body: {
              cell: (props: any) => (
                <td
                  {...props}
                  style={{
                    borderBottomWidth: "1px",
                    borderBottomColor: "#f0f0f0",
                    borderBottomStyle: "solid"
                  }}
                />
              )
            }
          }}
            dataSource={
              mergeDownloadChecked
                ? (isDoc2FormatEnabled && isPdf2FormatEnabled ? 
                    abstractFileFormatOptionsChecked : 
                    fileFormatOptionsChecked).map(format => ({
                      patientName: strJobDetails
                        .filter(job => strSelectedJobs.includes(job.jobId.toString()))
                        .map(job => job.patientName)
                        .join(", "),
                      jobId: strSelectedJobs.join(","),
                      fileFormats: format
                  }))
                : strJobDetails
                    .filter(job => strSelectedJobs.includes(job.jobId.toString()))
                    .flatMap(job =>
                      (isDoc2FormatEnabled && isPdf2FormatEnabled ? 
                      abstractFileFormatOptionsChecked : 
                      fileFormatOptionsChecked).map(format => ({
                        patientName: job.patientName,
                        jobId: job.jobId.toString(),
                        fileFormats: format
                      }))
                    )
            }
            pagination={false}>
            <Table.Column title="Patient Name" dataIndex="patientName" key="patientName" width={"33%"}/>
            <Table.Column
              title="Status"
              width={"25%"}
              key="status"
              render={(text, record) => (
                <>
                  {downloadStatus[
                    mergeDownloadChecked ? strSelectedJobs.join(",")+"-"+record.fileFormats : record.jobId+"-"+record.fileFormats
                  ]?.status || "Pending"}
                  {downloadStatus[
                    mergeDownloadChecked ? strSelectedJobs.join(",")+"-"+record.fileFormats : record.jobId+"-"+record.fileFormats
                  ]?.progress !== undefined && (
                    <>
                      <progress
                        value={
                          downloadStatus[
                            mergeDownloadChecked ? strSelectedJobs.join(",")+"-"+record.fileFormats : record.jobId+"-"+record.fileFormats
                          ].progress
                        }
                        max="100"></progress>
                      {` ${
                        downloadStatus[
                          mergeDownloadChecked ? strSelectedJobs.join(",")+"-"+record.fileFormats : record.jobId+"-"+record.fileFormats
                        ].progress
                      }%`}
                    </>
                  )}
                </>
              )}
            />
            <Table.Column title="File Format" dataIndex="fileFormats" key="fileFormats" width={"33%"}/>
          </Table>
          </Card>

          <div className="files-downloaded-info">
            Files Downloaded ({downloadedCount}/
            {(mergeDownloadChecked ? 1 : strSelectedJobs.length) * (isDoc2FormatEnabled && isPdf2FormatEnabled ? 
                      abstractFileFormatOptionsChecked : 
                      fileFormatOptionsChecked).length})
          </div>

          {downloadedCount > 0 && downloadedCount === strSelectedJobs.length && (
            <div className="download-complete-message">
              Files downloaded to {downloadDirectory?.name}
            </div>
          )}

          <div className="navigation-buttons">
            <Button type="primary" onClick={() => handleClose()}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DownloadModal;
